import { useSetModal } from "@/shared/atoms/useModal";
import useIsOrderEditingHook from "@/shared/hooks/useIsOrderEditingHook";
import useUserSettingsHook from "@/shared/hooks/useUserSettingsHook";
import usePosOrderPost from "@/shared/mutations/pos/usePosOrderPost";
import usePosOrderPut from "@/shared/mutations/pos/usePosOrderPut";
import { PaymentMethodEnum } from "@/shared/utils/enum";
import { useCallback } from "react";
import { useSetPosModal } from "../atoms/usePosModal";
import usePosOrderDeliveryMethod from "../atoms/usePosOrderDeliveryMethodAtom";
import { useSetPriorityModal } from "../atoms/usePriorityModal";
import { OrderV2 } from "../v2/queries/interfacesV2";
import useOpenCashDrawerHook from "./useOpenCashDrawerHook";
import usePosOrderResetHook from "./usePosOrderResetHook";
import usePosOrderSubmitDataHook from "./usePosOrderSubmitDataHook";
import { useIsTableMethod } from "./usePosOrderTableSessionHook";
import usePosSettingsHook from "./usePosSettingsHook";
import { PaymentMethodIdType } from "../utils/interfaces";
import usePosOrderTotalAmountHook from "./usePosOrderTotalAmountHook";
import { formatPrice } from "../utils/helpers";
import { useTranslation } from "react-i18next";
import useOrdersSessionByTableQuery from "../queries/orderssessions/useOrdersSessionByTableQuery";
import usePosOrderTable from "../atoms/usePosOrderTable";
import useIsWaiter from "./useIsWaiter";
import useCashDroConfigHook from "./useCashDroConfigHook";
import { logCashDro } from "../utils/cashDro";

const usePosOrderHook = (customPaymentId?: PaymentMethodIdType) => {
  const { t } = useTranslation();
  const query = useOrdersSessionByTableQuery();
  const isTableMethod = useIsTableMethod();
  const postOrder = usePosOrderPost();
  const putOrder = usePosOrderPut();
  const { isOrderEditing, setEditOrder } = useIsOrderEditingHook();
  const { isPosOrderPrintedAuto, isPosOrderAcceptedAuto, stickyPosOrder } =
    useUserSettingsHook();
  const setPriorityModal = useSetPriorityModal();
  const setModal = useSetModal();
  const [deliveryMethod] = usePosOrderDeliveryMethod();
  const { settings } = usePosSettingsHook();
  const openCashDrawer = useOpenCashDrawerHook();
  const setIsPosModal = useSetPosModal();
  const reset = usePosOrderResetHook();
  const submitData = usePosOrderSubmitDataHook();
  const totalAmount = usePosOrderTotalAmountHook();
  const [table] = usePosOrderTable();
  const isWaiter = useIsWaiter();
  const { isActive: isCashDroActive } = useCashDroConfigHook();

  const handlePostOrder = (
    isSplitPayment?: boolean,
    customerPaymentAmount?: string
  ) => {
    if (isOrderEditing) {
      return putOrder.mutateAsync(submitData({ customPaymentId }), {
        onSuccess: (data) => {
          if (isTableMethod && table?.item_id > 0) {
            query.refetch();
          }

          const paymentId = data.order_information.payment_method_id;
          const orderId = data.order_information.item_id;

          if (paymentId === PaymentMethodEnum.POS_TERMINAL) {
            setPriorityModal("pendingPayment", {
              orderId: orderId,
              eventName: `paymentSuccess-${orderId}`,
              callback: () => runAfterPostOrder(data, false, false, {}),
            });
          } else if (paymentId === PaymentMethodEnum.SPLIT_PAYMENT) {
            setEditOrder(data);
            return setModal("splitPayment", {
              totalAmount: totalAmount,
              orderId: data?.order_information?.item_id,
              isSession: false,
            });
          } else {
            runAfterPostOrder(data, false, false, {}, customerPaymentAmount);
          }
        },
      });
    } else {
      return postOrder.mutateAsync(submitData({ customPaymentId }), {
        onSuccess: (data) => {
          if (isTableMethod && table?.item_id > 0) {
            query.refetch();
          }

          const paymentId = data.order_information.payment_method_id;
          const orderId = data.order_information.item_id;
          if (paymentId === PaymentMethodEnum.POS_TERMINAL) {
            setEditOrder({ ...data, isEditing: false });
            setPriorityModal("pendingPayment", {
              orderId: orderId,
              eventName: `paymentSuccess-${orderId}`,
              callback: () => runAfterPostOrder(data, false, false, {}),
            });
          } else if (paymentId === PaymentMethodEnum.SPLIT_PAYMENT) {
            setEditOrder({ ...data, isEditing: false });
            return setModal("splitPayment", {
              totalAmount: totalAmount,
              orderId: data?.order_information?.item_id,
              isSession: false,
            });
          } else {
            runAfterPostOrder(data, false, false, {}, customerPaymentAmount);
          }
        },
      });
    }
  };

  const runAfterPostOrder = useCallback(
    (
      order?: OrderV2,
      resetTable = false,
      mustRefetch = false,
      {
        sessionId,
        sessionTotalAmount,
        sessionPaymentId,
      }: {
        sessionId?: number;
        sessionTotalAmount?: number;
        sessionPaymentId?: PaymentMethodIdType;
      } = {},
      customerPaymentAmount?: string
    ) => {
      if (mustRefetch) {
        query.refetch();
      }

      const total = sessionId ? sessionTotalAmount : totalAmount;
      const changeLabel = customerPaymentAmount
        ? `${t("posOrders.change")} ${formatPrice(
            parseFloat(customerPaymentAmount || "0") - total
          )}`
        : null;

      const printForPosOrder =
        isPosOrderAcceptedAuto && isPosOrderPrintedAuto === false;
      const showPrintPopup = !isWaiter && settings?.show_print_popup;

      if (sessionId && showPrintPopup) {
        setModal("printSessionReceipt", {
          sessionId: sessionId,
          customLabel: changeLabel,
        });
      } else if (
        order &&
        showPrintPopup &&
        (isOrderEditing || printForPosOrder)
      ) {
        setModal("printReceipt", { order, customLabel: changeLabel });
      } else if (changeLabel) {
        setModal("posOrderChangeLabel", { customLabel: changeLabel });
      } else {
        setModal("", {});
      }

      // With a CashDro the cash (and the change) goes through the terminal, not the drawer.
      if (
        order?.order_information?.payment_method_id ===
          PaymentMethodEnum.CASH ||
        sessionPaymentId === PaymentMethodEnum.CASH
      ) {
        if (isCashDroActive) {
          logCashDro("cash drawer not opened, CashDro handled the payment", {
            orderId: order?.order_information?.item_id,
            sessionId,
          });
        } else {
          openCashDrawer();
        }
      }
      if (stickyPosOrder === false) {
        setIsPosModal(false);
      }

      reset(
        resetTable ? resetTable : (settings?.reset_delivery_method ?? false),
        true
      );
    },
    [
      deliveryMethod,
      settings,
      isOrderEditing,
      isPosOrderAcceptedAuto,
      isPosOrderPrintedAuto,
      stickyPosOrder,
      totalAmount,
      isCashDroActive,
    ]
  );

  return {
    handlePostOrder,
    isPending: postOrder.isPending || putOrder.isPending,
    resetPosOrderStates: runAfterPostOrder,
  };
};

export default usePosOrderHook;
